type RGBColor = { r: number; g: number; b: number };
type ColorFormats = 'hex' | 'rgb' | 'rgba';

export interface ColorAnalysisOptions {
  /**
   * @default 5
   */
  samplingStep?: number;
  /**
   * @default 'hex'
   */
  format?: ColorFormats;
  /**
   * @default 1
   */
  alpha?: number;
}

export class ColorAnalyzer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    this.ctx = ctx;
  }

  async fromUrl(
    url: string,
    options?: ColorAnalysisOptions
  ): Promise<string | RGBColor> {
    const img = await this.loadImage(url);
    return this.analyze(img, options);
  }

  async fromFile(
    file: File,
    options?: ColorAnalysisOptions
  ): Promise<string | RGBColor> {
    const url = await this.fileToDataUrl(file);
    return this.fromUrl(url, options);
  }

  async fromImage(
    img: HTMLImageElement,
    options?: ColorAnalysisOptions
  ): Promise<string | RGBColor> {
    return this.analyze(img, options);
  }

  async fromBase64(
    base64: string,
    options?: ColorAnalysisOptions
  ): Promise<string | RGBColor> {
    if (!/^data:image\/[a-z]+;base64,/.test(base64)) {
      throw new Error('Invalid base64 image format. Expected data URI scheme');
    }
    return this.fromUrl(base64, options);
  }

  private async analyze(img: HTMLImageElement, options: ColorAnalysisOptions = {}) {
    const { samplingStep = 5, format = 'hex', alpha = 1 } = options;

    this.canvas.width = img.naturalWidth;
    this.canvas.height = img.naturalHeight;
    this.ctx.drawImage(img, 0, 0);

    const { data } = this.ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
    let r = 0,
      g = 0,
      b = 0,
      count = 0;

    for (let i = 0; i < data.length; i += 4 * samplingStep) {
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      count++;
    }

    const avgR = Math.round(r / count);
    const avgG = Math.round(g / count);
    const avgB = Math.round(b / count);

    return this.formatColor(avgR, avgG, avgB, format, alpha);
  }

  private formatColor(
    r: number,
    g: number,
    b: number,
    format: ColorFormats,
    alpha: number
  ) {
    switch (format) {
      case 'hex':
        return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
      case 'rgb':
        return `rgb(${r}, ${g}, ${b})`;
      case 'rgba':
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      default:
        return { r, g, b };
    }
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}
